import { useMemo } from 'react'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import { DateTime, Interval } from 'luxon'
import {
  Text,
  Card,
  Table,
  Label,
  Button,
  Accordion,
  Icon,
  CardColors,
} from '@/ui'
import { TypographyStyles } from '@/styles'

import {
  JournalEntry,
  JournalEntryLineItem,
  JournalEntryTransaction,
  JournalEntryType,
} from '../types'
import { getUserFinancialAccounts } from '@/selectors/financeSelectors'
import { filterObjNils } from '@/utils/typeHelpers'
import { getAccountName } from '../service'
import { useReselector, useToggle } from '@/utils/sharedHooks'
import { getAllTransactionCategories } from '@/features/reports/reports.selectors'
import { centsToDollars } from '@/utils/currencyHelpers'
import { CurrencyFormatLabel } from '@/components/CurrencyFormatLabel'
import { DATE_FORMATS_LUXON, isoToUTCDateTime } from '@/utils/dateHelpers'
import { EditJournalEntryModal } from './EditJournalEntryModal'
import { DeleteJournalEntryModal } from './DeleteJournalEntryModal'

const JournalEntryLine = ({
  lineItem,
  userId,
}: {
  lineItem: JournalEntryLineItem
  userId: number
}) => {
  const {
    financialAccountId,
    transactionCategoryId,
    entryType,
    amountInCents,
  } = lineItem

  const financialAccountsObj = useReselector(
    getUserFinancialAccounts,
    userId ?? 0
  )
  const financialAccountsById = useMemo(
    () => filterObjNils(financialAccountsObj),
    [financialAccountsObj]
  )
  const transactionCategoriesById = useReselector(getAllTransactionCategories)

  const accountName = useMemo(() => {
    return getAccountName({
      financialAccountsById,
      financialAccountId: `${financialAccountId ?? ''}`,
      transactionCategoriesById,
      transactionCategoryId: `${transactionCategoryId ?? ''}`,
    })
  }, [
    financialAccountId,
    transactionCategoryId,
    transactionCategoriesById,
    financialAccountsById,
  ])

  return (
    <Table.Row>
      <Table.Cell>
        <Text variant={TypographyStyles.BODY_SM}>{accountName}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text variant={TypographyStyles.BODY_SM}>
          {entryType === JournalEntryType.DEBIT ? (
            <CurrencyFormatLabel value={centsToDollars(amountInCents)} />
          ) : (
            ''
          )}
        </Text>
      </Table.Cell>
      <Table.Cell>
        <Text variant={TypographyStyles.BODY_SM}>
          {entryType === JournalEntryType.CREDIT ? (
            <CurrencyFormatLabel value={centsToDollars(amountInCents)} />
          ) : (
            ''
          )}
        </Text>
      </Table.Cell>
    </Table.Row>
  )
}

export const JournalEntryLinesTable = ({
  journalEntryLines,
  userId,
}: {
  journalEntryLines: JournalEntryLineItem[]
  userId: number
}) => {
  return (
    <Table className="mt-4">
      <Table.Header>
        <Table.Row>
          <Table.HeaderCell className="text-sm text-muted-foreground">
            Account
          </Table.HeaderCell>
          <Table.HeaderCell className="text-sm text-muted-foreground">
            Debit
          </Table.HeaderCell>
          <Table.HeaderCell className="text-sm text-muted-foreground">
            Credit
          </Table.HeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {journalEntryLines.map((lineItem) => (
          <JournalEntryLine
            key={lineItem.id}
            lineItem={lineItem}
            userId={userId}
          />
        ))}
      </Table.Body>
    </Table>
  )
}

export const JournalEntryCard = ({
  journalEntry,
  userId,
  deleting = false,
}: {
  journalEntry: JournalEntry
  userId: number
  deleting?: boolean
}) => {
  const [isEditing, toggleEdit] = useToggle()
  const [isDeleting, toggleDelete] = useToggle()

  const { journalEntryDate, details, journalEntryLines, automated } =
    journalEntry

  return (
    <Card color={deleting ? CardColors.LEVEL_1 : CardColors.LEVEL_0}>
      <div className="flex flex-col md:flex-row justify-between mb-4">
        <div className="flex-1">
          <Text variant={TypographyStyles.H3} className="mb-2">
            {isoToUTCDateTime(journalEntryDate).toFormat(
              DATE_FORMATS_LUXON.INPUT
            )}
          </Text>
          <Text className="text-muted-foreground italic">{details}</Text>
        </div>

        {!deleting && (
          <div className="flex gap-4 items-center mt-4 md:mt-0">
            {automated ? (
              <Label>
                <Icon icon={regular('bolt')} /> Auto
              </Label>
            ) : (
              <Label>
                <Icon icon={regular('hand')} /> Manual
              </Label>
            )}
            <Button
              variant="discreet"
              onClick={toggleEdit}
              iconLeft={regular('edit')}
            />
            {!automated && (
              <Button
                variant="discreet"
                onClick={toggleDelete}
                iconLeft={regular('trash-alt')}
              />
            )}
          </div>
        )}
      </div>

      <JournalEntryLinesTable
        journalEntryLines={journalEntryLines}
        userId={userId}
      />

      {isEditing && (
        <EditJournalEntryModal
          open={isEditing}
          onClose={toggleEdit}
          journalEntry={journalEntry}
        />
      )}
      {isDeleting && (
        <DeleteJournalEntryModal
          open={isDeleting}
          onClose={toggleDelete}
          journalEntry={journalEntry}
          userId={userId}
        />
      )}
    </Card>
  )
}

export const JournalEntryTransactionRow = ({
  transaction,
  userId,
  dateRange,
}: {
  transaction: JournalEntryTransaction
  userId: number
  dateRange: [string, string]
}) => {
  const [isCreating, toggleCreate] = useToggle()

  const { journalEntriesWithinRange, journalEntriesOutsideRange } =
    useMemo(() => {
      if (!dateRange?.[0] || !dateRange?.[1])
        return {
          journalEntriesWithinRange: transaction.journalEntries,
          journalEntriesOutsideRange: [],
        }

      const startDate = DateTime.fromFormat(
        dateRange[0],
        DATE_FORMATS_LUXON.INPUT,
        { zone: 'utc' }
      ).startOf('day')
      const endDate = DateTime.fromFormat(
        dateRange[1],
        DATE_FORMATS_LUXON.INPUT,
        { zone: 'utc' }
      ).endOf('day')
      const rangeInterval = Interval.fromDateTimes(startDate, endDate)

      const entriesWithinRange = transaction.journalEntries.filter(
        (journalEntry) =>
          rangeInterval.contains(
            isoToUTCDateTime(journalEntry.journalEntryDate)
          )
      )
      const entriesOutsideRange = transaction.journalEntries.filter(
        (journalEntry) =>
          !rangeInterval.contains(
            isoToUTCDateTime(journalEntry.journalEntryDate)
          )
      )

      return {
        journalEntriesWithinRange: entriesWithinRange,
        journalEntriesOutsideRange: entriesOutsideRange,
      }
    }, [transaction.journalEntries, dateRange])

  return (
    <div className="flex flex-col md:flex-row gap-6 py-4">
      <div className="w-full md:w-1/3">
        {transaction.type === 'without_transaction' ? (
          <>
            <Text variant={TypographyStyles.H3} className="mb-2">
              N/A
            </Text>
            <Text>
              These journal entries aren&apos;t linked to a transaction
            </Text>
          </>
        ) : (
          <>
            <Text variant={TypographyStyles.H3} className="mb-2">
              {transaction.description}
            </Text>
            <Text className="mb-2">ID: {transaction.id}</Text>
            <Button variant="secondary" onClick={toggleCreate} size="sm">
              Create Journal Entry
            </Button>
            {isCreating && (
              <EditJournalEntryModal
                open={isCreating}
                onClose={toggleCreate}
                transactionId={transaction.id?.toString()}
              />
            )}
          </>
        )}
      </div>

      <div className="w-full md:w-2/3 space-y-4">
        {journalEntriesWithinRange.map((journalEntry) => (
          <JournalEntryCard
            key={journalEntry.id}
            journalEntry={journalEntry}
            userId={userId}
          />
        ))}

        {journalEntriesOutsideRange.length > 0 && (
          <Accordion variant="text" type="single" collapsible>
            <Accordion.Item value="journal-entries-outside-range">
              <Accordion.Trigger>
                <Text>
                  {`${journalEntriesOutsideRange.length} journal entr${
                    journalEntriesOutsideRange.length === 1 ? 'y' : 'ies'
                  } outside of date filter`}
                </Text>
              </Accordion.Trigger>
              <Accordion.Content>
                <>
                  {journalEntriesOutsideRange.map((journalEntry) => (
                    <JournalEntryCard
                      key={journalEntry.id}
                      journalEntry={journalEntry}
                      userId={userId}
                    />
                  ))}
                </>
              </Accordion.Content>
            </Accordion.Item>
          </Accordion>
        )}
      </div>
    </div>
  )
}
